import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowRight,
  Camera,
  CheckCircle,
  Clock,
  MapPin,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAttendance, useGeolocation } from '@/hooks';
import type { ClockInResponse, ClockOutResponse } from '@/types';
import { formatNaira, formatStatus, hasLateClockInDeductions } from '@/utils';
import { cn } from '@/utils/cn';
import { WebcamCapture } from './WebcamCapture';

interface ClockInButtonProps {
  type: 'clock-in' | 'clock-out';
  timeRestriction: { isAllowed: boolean; message?: string } | null;
}

export function ClockInButton({ type, timeRestriction }: ClockInButtonProps) {
  const [showCapture, setShowCapture] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<ClockInResponse | ClockOutResponse | null>(null);
  const { clockIn, clockOut, isLoading, error, canClockIn, canClockOut, fetchPaymentRecord } =
    useAttendance(false);
  const { getLocation } = useGeolocation();

  const isClockIn = type === 'clock-in';

  const isDisabled = isClockIn ? !canClockIn || !timeRestriction?.isAllowed : !canClockOut;

  const handleClick = () => {
    setShowCapture(true);
  };

  const handleCapture = async (imageData: string) => {
    try {
      const location = await getLocation();

      const data = {
        photo: imageData,
        location: location || undefined,
      };

      let response;
      if (isClockIn) {
        response = await clockIn(data);
        if (hasLateClockInDeductions(response)) {
          await fetchPaymentRecord();
        }
      } else {
        response = await clockOut(data);
      }

      setResult(response);
      setShowCapture(false);
      setShowResult(true);
    } catch {}
  };

  const handleCancel = () => {
    setShowCapture(false);
  };

  const handleResultClose = () => {
    setShowResult(false);
    setResult(null);
  };

  return (
    <>
      {!(isClockIn && !timeRestriction?.isAllowed) && (
        <button
          type="button"
          onClick={handleClick}
          disabled={isDisabled || isLoading}
          className={cn(
            'relative group overflow-hidden px-6 py-3.5 rounded-xl font-black text-lg transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl flex items-center gap-3',
            isClockIn
              ? 'bg-neolab-green text-white shadow-green-500/30 hover:bg-neolab-dark-green'
              : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border-2 border-slate-200 dark:border-slate-700 hover:border-neolab-green shadow-slate-500/10',
          )}
        >
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
          {isLoading ? (
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Clock className={cn('w-6 h-6', isClockIn ? 'text-white' : 'text-neolab-green')} />
          )}
          <span className="relative z-10">{isClockIn ? 'Clock In' : 'Clock Out'}</span>
          <ArrowRight
            className={cn(
              'w-5 h-5 transition-transform group-hover:translate-x-1',
              isClockIn ? 'text-white/50' : 'text-slate-300',
            )}
          />
        </button>
      )}

      {showCapture &&
        createPortal(
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={handleCancel}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-2xl bg-white dark:bg-neolab-forest rounded-2xl shadow-2xl overflow-hidden border border-white/10"
            >
              <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-neolab-green/10">
                      <Camera className="w-5 h-5 text-neolab-green" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                        Identity Verification
                      </h3>
                      <p className="text-xs text-slate-500 font-medium">
                        Please look into the camera to {isClockIn ? 'clock in' : 'clock out'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-rose-500 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-6 p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 flex items-center gap-2.5"
                  >
                    <AlertCircle className="w-4 h-4 text-rose-500" />
                    <p className="text-xs text-rose-700 dark:text-rose-300 font-bold">{error}</p>
                  </motion.div>
                )}

                <div className="rounded-xl overflow-hidden border-2 border-slate-100 dark:border-neolab-forest shadow-inner bg-slate-50 dark:bg-neolab-deep-green">
                  <WebcamCapture onCapture={handleCapture} onCancel={handleCancel} countdown={3} />
                </div>

                <div className="mt-6 flex items-center justify-center gap-4 text-slate-400">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                    <MapPin className="w-4 h-4 text-neolab-green" />
                    Location Enabled
                  </div>
                  <div className="w-1 h-1 rounded-full bg-slate-300" />
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                    <ShieldCheck className="w-4 h-4 text-neolab-green" />
                    Secure Session
                  </div>
                </div>
              </div>
            </motion.div>
          </div>,
          document.getElementById('modal-root') ?? document.body,
        )}

      {showResult &&
        result &&
        createPortal(
          <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 pointer-events-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/90 backdrop-blur-xl"
              onClick={handleResultClose}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 40 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-2xl p-8 text-center shadow-2xl border border-white/5"
            >
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 bg-neolab-green rounded-full animate-ping opacity-20" />
                <div className="relative w-20 h-20 rounded-full bg-neolab-green flex items-center justify-center shadow-xl shadow-green-500/40">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              </div>

              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                {isClockIn ? 'Welcome!' : 'Goodbye!'}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mb-6">
                {isClockIn
                  ? 'Your attendance has been recorded successfully.'
                  : "You've clocked out for the day. Rest well!"}
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Time Recorded
                  </span>
                  <span className="text-base font-black text-slate-900 dark:text-white">
                    {isClockIn && 'clockInTime' in result.attendance
                      ? result.attendance.clockInTime
                      : !isClockIn && 'clockOutTime' in result.attendance
                        ? result.attendance.clockOutTime
                        : ''}
                  </span>
                </div>

                {isClockIn && 'minutesLate' in result.attendance && (
                  <>
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Status
                      </span>
                      <span
                        className={cn(
                          'text-xs font-black px-2.5 py-1 rounded-lg',
                          result.attendance.minutesLate > 0
                            ? 'bg-rose-500/10 text-rose-500'
                            : 'bg-emerald-500/10 text-emerald-500',
                        )}
                      >
                        {formatStatus(result.attendance.lateCategory || 'on_time')}
                      </span>
                    </div>

                    {'totalDeductions' in result && result.totalDeductions > 0 && (
                      <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/30 text-left">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest">
                            Total Deductions
                          </span>
                          <span className="text-lg font-black text-rose-600">
                            {formatNaira(result.totalDeductions)}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {'deductions' in result &&
                            result.deductions?.map((d, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between text-xs font-bold text-rose-400"
                              >
                                <span>• {d.reason}</span>
                                <span>{formatNaira(d.amount)}</span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                type="button"
                onClick={handleResultClose}
                className="w-full py-3.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black text-base hover:scale-[1.02] active:scale-95 transition-all duration-200 shadow-xl"
              >
                Continue to Dashboard
              </button>
            </motion.div>
          </div>,
          document.getElementById('modal-root') ?? document.body,
        )}
    </>
  );
}

export default ClockInButton;
